/**
 * BroadcastStepper – Fluxo de criação de transmissão WhatsApp por etapas
 * 4 etapas: Destinatários → Conteúdo → Agendamento → Revisão
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useRoute } from "wouter";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { MobileBlockerGate } from "@/components/MobileBlocker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Pencil,
  Send,
  Users,
  FileText,
  Calendar,
  ClipboardCheck,
  Save,
  Tag,
  X,
  Filter,
  Clock,
  Rocket,
  Loader2,
  Radio,
  Eye,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

// ─── Types ──────────────────────────────────────────────────────────────────

type StepKey = "recipients" | "content" | "schedule" | "review";

interface StepConfig {
  key: StepKey;
  icon: React.ElementType;
  title: string;
  description: string;
}

const STEPS: StepConfig[] = [
  {
    key: "recipients",
    icon: Users,
    title: "Destinatários",
    description: "Selecione quem receberá esta transmissão",
  },
  {
    key: "content",
    icon: FileText,
    title: "Conteúdo",
    description: "Selecione o fluxo que será executado para cada destinatário",
  },
  {
    key: "schedule",
    icon: Calendar,
    title: "Agendamento",
    description: "Escolha quando a transmissão será disparada",
  },
  {
    key: "review",
    icon: ClipboardCheck,
    title: "Revisão",
    description: "Revise todos os detalhes antes de enviar",
  },
];

const BRAZIL_STATES = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS",
  "MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

// ─── Step completeness ──────────────────────────────────────────────────────

interface BroadcastData {
  flowId: number | null;
  selectedTagIds: number[];
  segmentId: number | null;
  scheduleType: "now" | "scheduled";
  scheduledDate: string;
  scheduledTime: string;
  notes: string;
}

function isStepComplete(step: StepKey, data: BroadcastData): boolean {
  switch (step) {
    case "recipients":
      return data.selectedTagIds.length > 0 || !!data.segmentId;
    case "content":
      return !!data.flowId;
    case "schedule":
      if (data.scheduleType === "now") return true;
      return !!data.scheduledDate && !!data.scheduledTime;
    case "review":
      return isStepComplete("recipients", data) && isStepComplete("content", data) && isStepComplete("schedule", data);
  }
}

// ─── Auto-save hook ─────────────────────────────────────────────────────────

function useAutoSave(campaignId: number | undefined, dirty: boolean, save: () => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!campaignId || !dirty) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, 2000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [campaignId, dirty, save]);
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function BroadcastStepper() {
  return (
    <MobileBlockerGate featureName="Criação de Transmissão">
      <BroadcastStepperImpl />
    </MobileBlockerGate>
  );
}

function BroadcastStepperImpl() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/broadcast/:id");
  const isCreateRoute = params?.id === "create";
  const campaignId = params?.id && !isCreateRoute ? parseInt(params.id, 10) : undefined;

  // ── Local state ───────────────────────────────────────────────────────────
  const [expandedStep, setExpandedStep] = useState<StepKey | null>("recipients");
  const [editingName, setEditingName] = useState(false);
  const [localName, setLocalName] = useState("Nova Transmissão");
  const [flowId, setFlowId] = useState<number | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [segmentId, setSegmentId] = useState<number | null>(null);
  const [scheduleType, setScheduleType] = useState<"now" | "scheduled">("now");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("09:00");
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: campaign, isLoading } = trpc.campaigns.get.useQuery(
    { id: campaignId! },
    { enabled: !!campaignId }
  );
  const { data: tags = [] } = trpc.tags.list.useQuery();
  const { data: segments = [] } = trpc.segments.list.useQuery();
  const { data: flowBuilders = [] } = trpc.flows.listFlowBuilders.useQuery();

  const activeFlows = useMemo(
    () => (flowBuilders as any[]).filter((f) => f.status === "active"),
    [flowBuilders]
  );
  const selectedFlow = useMemo(
    () => (flowBuilders as any[]).find((f) => f.id === flowId),
    [flowBuilders, flowId]
  );

  const utils = trpc.useUtils();

  // ── Auto-create on /broadcast/create ──────────────────────────────────────
  const createMutation = trpc.campaigns.create.useMutation({
    onSuccess: (data) => {
      if (data?.id) navigate(`/broadcast/${data.id}`, { replace: true });
    },
    onError: (err) => {
      toast.error(toUserFriendlyError(err, "Erro ao criar"));
      navigate("/broadcast");
    },
  });

  useEffect(() => {
    if (isCreateRoute && !createMutation.isPending) {
      createMutation.mutate({
        name: "Nova Transmissão",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateRoute]);

  const updateMutation = trpc.campaigns.update.useMutation({
    onSuccess: () => {
      setDirty(false);
      setLastSaved(new Date());
      if (campaignId) utils.campaigns.get.invalidate({ id: campaignId });
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao salvar")),
  });

  const sendCampaignMut = trpc.whatsapp.sendCampaign.useMutation({
    onSuccess: (data) => {
      setSendModalOpen(false);
      const total = data?.total ?? 0;
      toast.success(
        total > 0
          ? `Transmissão iniciada para ${total} ${total === 1 ? "contato" : "contatos"}. Acompanhe o progresso na lista.`
          : "Transmissão iniciada. Acompanhe o progresso na lista.",
      );
      utils.campaigns.list.invalidate();
      navigate("/broadcast");
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao enviar")),
  });

  // ── Sync server → local on load ──────────────────────────────────────────
  useEffect(() => {
    if (!campaign) return;
    setLocalName(campaign.name ?? "");
    setFlowId(campaign.flowId ?? null);
    setSelectedTagIds((campaign.targetTagIds as number[]) ?? []);
    setSegmentId(campaign.segmentId ?? null);
    setNotes(campaign.notes ?? "");
    if (campaign.scheduledAt) {
      setScheduleType("scheduled");
      const d = new Date(campaign.scheduledAt);
      setScheduledDate(d.toISOString().split("T")[0]);
      setScheduledTime(d.toTimeString().slice(0, 5));
    }
  }, [campaign]);

  // ── Save function ─────────────────────────────────────────────────────────
  const saveChanges = useCallback(() => {
    if (!campaignId) return;
    const payload: any = {
      id: campaignId,
      name: localName,
      notes: notes || null,
      targetTagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
      segmentId: segmentId ?? null,
      flowId: flowId ?? null,
    };
    if (scheduleType === "scheduled" && scheduledDate && scheduledTime) {
      payload.scheduledAt = new Date(`${scheduledDate}T${scheduledTime}:00`);
      payload.status = "scheduled";
    }
    updateMutation.mutate(payload);
  }, [campaignId, localName, flowId, selectedTagIds, segmentId, notes, scheduleType, scheduledDate, scheduledTime, updateMutation]);

  useAutoSave(campaignId, dirty, saveChanges);

  const markDirty = () => setDirty(true);
  const toggleStep = (key: StepKey) => setExpandedStep((prev) => (prev === key ? null : key));

  // ── Build data for completeness ───────────────────────────────────────────
  const data: BroadcastData = {
    flowId,
    selectedTagIds,
    segmentId,
    scheduleType,
    scheduledDate,
    scheduledTime,
    notes,
  };

  const allReady = isStepComplete("review", data);

  const toggleTag = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
    markDirty();
  };

  // ── Contact count preview ─────────────────────────────────────────────────
  const hasContactFilters = selectedTagIds.length > 0 || !!segmentId;
  const { data: previewData } = trpc.contacts.list.useQuery(
    { limit: 1 } as any,
    { enabled: false } // just keep type happy; we'll rely on segment count
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdvance = () => {
    if (!allReady) {
      toast.warning("Complete todas as etapas antes de avançar.");
      return;
    }
    saveChanges();
    setSendModalOpen(true);
  };

  const handleSendNow = () => {
    if (!campaignId) return;
    sendCampaignMut.mutate({ campaignId });
  };

  const handleSchedule = () => {
    if (!campaignId || !scheduledDate || !scheduledTime) {
      toast.warning("Selecione data e hora.");
      return;
    }
    const dt = new Date(`${scheduledDate}T${scheduledTime}:00`);
    if (dt <= new Date()) {
      toast.warning("A data deve ser no futuro.");
      return;
    }
    updateMutation.mutate(
      { id: campaignId, status: "scheduled", scheduledAt: dt },
      {
        onSuccess: () => {
          setSendModalOpen(false);
          toast.success(`Transmissão agendada para ${dt.toLocaleString("pt-BR")}`);
          navigate("/broadcast");
        },
      }
    );
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isCreateRoute || isLoading || (!campaign && campaignId)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  // ── Step summaries ────────────────────────────────────────────────────────
  function getStepSummary(step: StepKey): string {
    switch (step) {
      case "recipients": {
        const parts: string[] = [];
        if (segmentId) {
          const seg = (segments as any[]).find((s) => s.id === segmentId);
          parts.push(seg ? `Segmento: ${seg.name}` : "1 segmento");
        }
        if (selectedTagIds.length > 0) parts.push(`${selectedTagIds.length} tag(s)`);
        return parts.length > 0 ? parts.join(", ") : "Nenhum destinatário selecionado";
      }
      case "content":
        return selectedFlow ? selectedFlow.name : "Nenhum fluxo selecionado";
      case "schedule":
        if (scheduleType === "now") return "Enviar imediatamente";
        if (scheduledDate && scheduledTime) {
          const d = new Date(`${scheduledDate}T${scheduledTime}:00`);
          return `Agendado: ${d.toLocaleString("pt-BR")}`;
        }
        return "Horário não definido";
      case "review":
        return allReady ? "Pronto para enviar" : "Etapas pendentes";
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="space-y-0">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b bg-background px-6 py-3 -mx-5 -mt-5 md:-mx-6 md:-mt-6 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate("/broadcast")} className="shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>

            {editingName ? (
              <Input
                value={localName}
                onChange={(e) => { setLocalName(e.target.value); markDirty(); }}
                onBlur={() => { setEditingName(false); saveChanges(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { setEditingName(false); saveChanges(); } }}
                autoFocus
                className="max-w-xs text-sm font-medium"
              />
            ) : (
              <button
                onClick={() => setEditingName(true)}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors min-w-0"
              >
                <Radio className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{localName}</span>
                <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            )}

            {lastSaved && (
              <span className="text-xs text-muted-foreground whitespace-nowrap hidden md:inline">
                Salvo às {lastSaved.toLocaleString("pt-BR")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => { saveChanges(); toast.success("Salvo!"); }}>
              <Save className="h-3.5 w-3.5" />
              Salvar
            </Button>
            <Button size="sm" className="gap-2" onClick={handleAdvance} disabled={!allReady}>
              <Send className="h-3.5 w-3.5" />
              Enviar / Agendar
            </Button>
          </div>
        </div>

        {/* ── Steps ──────────────────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto space-y-3 px-4">
          {STEPS.map((step, index) => {
            const complete = isStepComplete(step.key, data);
            const isExpanded = expandedStep === step.key;
            const Icon = step.icon;

            return (
              <Card key={step.key} className="overflow-hidden">
                {/* Step header */}
                <button
                  onClick={() => toggleStep(step.key)}
                  className="flex items-center gap-4 w-full p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <div
                    className={`flex items-center justify-center h-9 w-9 rounded-full shrink-0 ${
                      complete
                        ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-primary/10 text-primary"
                    }`}
                  >
                    {complete ? <Check className="h-4 w-4" /> : <span className="text-sm font-semibold">{index + 1}</span>}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-sm">{step.title}</h3>
                      {complete && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Completo</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {isExpanded ? step.description : getStepSummary(step.key)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!isExpanded && <span className="text-xs text-primary font-medium hidden sm:inline">Editar</span>}
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </button>

                {/* Step content */}
                {isExpanded && (
                  <>
                    <Separator />
                    <div className="p-4 space-y-4">
                      {step.key === "recipients" && (
                        <RecipientsStep
                          tags={tags as any[]}
                          segments={segments as any[]}
                          selectedTagIds={selectedTagIds}
                          segmentId={segmentId}
                          onToggleTag={toggleTag}
                          onChangeSegment={(v) => { setSegmentId(v); markDirty(); }}
                        />
                      )}
                      {step.key === "content" && (
                        <ContentStep
                          flows={activeFlows}
                          allFlows={flowBuilders as any[]}
                          flowId={flowId}
                          onChangeFlow={(v) => { setFlowId(v); markDirty(); }}
                        />
                      )}
                      {step.key === "schedule" && (
                        <ScheduleStep
                          scheduleType={scheduleType}
                          scheduledDate={scheduledDate}
                          scheduledTime={scheduledTime}
                          onChangeType={(v) => { setScheduleType(v); markDirty(); }}
                          onChangeDate={(v) => { setScheduledDate(v); markDirty(); }}
                          onChangeTime={(v) => { setScheduledTime(v); markDirty(); }}
                          notes={notes}
                          onChangeNotes={(v) => { setNotes(v); markDirty(); }}
                        />
                      )}
                      {step.key === "review" && (
                        <ReviewStep
                          data={data}
                          flowName={selectedFlow?.name}
                          segmentName={(segments as any[]).find((s) => s.id === segmentId)?.name}
                          tagCount={selectedTagIds.length}
                        />
                      )}
                    </div>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Modal de Envio / Agendamento ─────────────────────────────────── */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar Transmissão</DialogTitle>
            <DialogDescription>
              Confirme o envio da transmissão "{localName}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <button
              onClick={() => setScheduleType("now")}
              className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-colors ${
                scheduleType === "now" ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30"
              }`}
            >
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary shrink-0">
                <Rocket className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-sm">Enviar agora</p>
                <p className="text-xs text-muted-foreground">A transmissão será disparada imediatamente</p>
              </div>
            </button>

            <button
              onClick={() => setScheduleType("scheduled")}
              className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-colors ${
                scheduleType === "scheduled" ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30"
              }`}
            >
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600 shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-sm">Agendar para depois</p>
                <p className="text-xs text-muted-foreground">Escolha data e horário para o disparo</p>
              </div>
            </button>

            {scheduleType === "scheduled" && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Horário</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModalOpen(false)}>Cancelar</Button>
            {scheduleType === "now" ? (
              <Button onClick={handleSendNow} disabled={sendCampaignMut.isPending} className="gap-2">
                {sendCampaignMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                Enviar agora
              </Button>
            ) : (
              <Button onClick={handleSchedule} disabled={updateMutation.isPending} className="gap-2">
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                Agendar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// ─── Step: Recipients ───────────────────────────────────────────────────────

function RecipientsStep({
  tags,
  segments,
  selectedTagIds,
  segmentId,
  onToggleTag,
  onChangeSegment,
}: {
  tags: any[];
  segments: any[];
  selectedTagIds: number[];
  segmentId: number | null;
  onToggleTag: (id: number) => void;
  onChangeSegment: (id: number | null) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Segmento */}
      <div>
        <Label className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5" />
          Segmento
        </Label>
        <Select
          value={segmentId?.toString() || "_none"}
          onValueChange={(v) => onChangeSegment(v === "_none" ? null : Number(v))}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Selecionar segmento..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">Nenhum segmento</SelectItem>
            {segments.map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {s.name} ({s.contactCount || 0} contatos)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Se selecionado, sobrescreve a segmentação por tags
        </p>
      </div>

      {/* Tags */}
      <div>
        <Label className="flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5" />
          Segmentação por Tags
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          Selecione tags para filtrar os contatos. Sem seleção, todos os contatos ativos receberão a mensagem.
        </p>
        {tags.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Nenhuma tag criada ainda.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => {
              const isSelected = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  onClick={() => onToggleTag(tag.id)}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-all border ${
                    isSelected ? "ring-2 ring-offset-1" : "opacity-60 hover:opacity-100"
                  }`}
                  style={{
                    backgroundColor: (tag.color ?? "#25D366") + "20",
                    color: tag.color ?? "#25D366",
                    borderColor: (tag.color ?? "#25D366") + "60",
                  }}
                >
                  <Tag className="h-3 w-3" />
                  {tag.name}
                  {isSelected && <X className="h-3 w-3 ml-0.5" />}
                </button>
              );
            })}
          </div>
        )}
        {selectedTagIds.length > 0 && !segmentId && (
          <p className="text-xs text-primary mt-2 font-medium">
            {selectedTagIds.length} tag(s) selecionada(s)
          </p>
        )}
        {segmentId && (
          <p className="text-xs text-blue-600 mt-2 font-medium">
            Segmento selecionado — tags serão ignoradas
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Step: Content ──────────────────────────────────────────────────────────

function ContentStep({
  flows,
  allFlows,
  flowId,
  onChangeFlow,
}: {
  flows: any[];
  allFlows: any[];
  flowId: number | null;
  onChangeFlow: (id: number) => void;
}) {
  const [, navigate] = useLocation();
  const selected = allFlows.find((f) => f.id === flowId);

  return (
    <div className="space-y-4">
      <div>
        <Label>Fluxo</Label>
        <Select
          value={flowId?.toString() || ""}
          onValueChange={(v) => onChangeFlow(Number(v))}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Selecionar fluxo..." />
          </SelectTrigger>
          <SelectContent>
            {flows.length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground text-center">
                Nenhum fluxo ativo disponível.
              </div>
            ) : (
              flows.map((f) => (
                <SelectItem key={f.id} value={String(f.id)}>
                  <span className="flex items-center gap-2">
                    {f.name}
                    {f.description && (
                      <span className="text-muted-foreground">— {f.description}</span>
                    )}
                  </span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Preview do fluxo selecionado */}
      {selected && (
        <div className="p-3 bg-muted/40 rounded-lg border space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fluxo selecionado</p>
          <p className="font-medium text-sm">{selected.name}</p>
          {selected.description && (
            <p className="text-sm text-muted-foreground">{selected.description}</p>
          )}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{selected.triggerType}</Badge>
            <Badge variant="secondary" className="text-xs">
              {selected.totalExecutions ?? 0} execuções
            </Badge>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 dark:bg-blue-950/30 dark:border-blue-900 dark:text-blue-300">
        <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0" />
        <p>
          A transmissão executará este fluxo para cada destinatário. Se o fluxo começa com um template,
          certifique-se de que ele está aprovado pela Meta (regra das 24h).
        </p>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => navigate("/whatsapp-flows")}
      >
        <FileText className="h-3.5 w-3.5" />
        Criar novo fluxo
      </Button>
    </div>
  );
}

// ─── Step: Schedule ─────────────────────────────────────────────────────────

function ScheduleStep({
  scheduleType,
  scheduledDate,
  scheduledTime,
  onChangeType,
  onChangeDate,
  onChangeTime,
  notes,
  onChangeNotes,
}: {
  scheduleType: "now" | "scheduled";
  scheduledDate: string;
  scheduledTime: string;
  onChangeType: (v: "now" | "scheduled") => void;
  onChangeDate: (v: string) => void;
  onChangeTime: (v: string) => void;
  notes: string;
  onChangeNotes: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={() => onChangeType("now")}
          className={`flex-1 py-3 px-3 rounded-lg border text-sm font-medium transition-all ${
            scheduleType === "now"
              ? "border-primary bg-primary/10 text-primary"
              : "border-muted-foreground/20 text-muted-foreground hover:border-primary/40"
          }`}
        >
          <Send className="h-4 w-4 mx-auto mb-1" />
          Enviar agora
        </button>
        <button
          onClick={() => onChangeType("scheduled")}
          className={`flex-1 py-3 px-3 rounded-lg border text-sm font-medium transition-all ${
            scheduleType === "scheduled"
              ? "border-primary bg-primary/10 text-primary"
              : "border-muted-foreground/20 text-muted-foreground hover:border-primary/40"
          }`}
        >
          <Clock className="h-4 w-4 mx-auto mb-1" />
          Agendar
        </button>
      </div>

      {scheduleType === "scheduled" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Data</Label>
            <Input
              type="date"
              value={scheduledDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => onChangeDate(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Horário</Label>
            <Input
              type="time"
              value={scheduledTime}
              onChange={(e) => onChangeTime(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      )}

      <div>
        <Label>Observações (opcional)</Label>
        <Textarea
          placeholder="Notas internas sobre esta transmissão..."
          value={notes}
          onChange={(e) => onChangeNotes(e.target.value)}
          className="mt-1 min-h-[60px]"
        />
      </div>
    </div>
  );
}

// ─── Step: Review ───────────────────────────────────────────────────────────

function ReviewStep({
  data,
  flowName,
  segmentName,
  tagCount,
}: {
  data: BroadcastData;
  flowName?: string;
  segmentName?: string;
  tagCount: number;
}) {
  const rows = [
    {
      label: "Destinatários",
      value: data.segmentId
        ? `Segmento: ${segmentName ?? data.segmentId}`
        : tagCount > 0
        ? `${tagCount} tag(s) selecionada(s)`
        : "Todos os contatos",
      ok: isStepComplete("recipients", data),
    },
    {
      label: "Fluxo",
      value: flowName ?? "Não selecionado",
      ok: isStepComplete("content", data),
    },
    {
      label: "Agendamento",
      value:
        data.scheduleType === "now"
          ? "Enviar imediatamente"
          : data.scheduledDate && data.scheduledTime
          ? new Date(`${data.scheduledDate}T${data.scheduledTime}:00`).toLocaleString("pt-BR")
          : "Não definido",
      ok: isStepComplete("schedule", data),
    },
  ];

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground">{row.label}</p>
            <p className="text-sm font-medium">{row.value}</p>
          </div>
          {row.ok ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertCircle className="h-5 w-5 text-amber-500" />
          )}
        </div>
      ))}

      {data.notes && (
        <div className="p-3 bg-muted/30 rounded-lg">
          <p className="text-xs text-muted-foreground">Observações</p>
          <p className="text-sm">{data.notes}</p>
        </div>
      )}
    </div>
  );
}
